/**
 * Client-Side Entity Index for Navigational Search
 *
 * Provides instant (< 10ms) fuzzy matching for navigational queries.
 * Caches entity metadata from multiple APIs for offline-capable search.
 *
 * The entity catalog is fetched once and cached for 5 minutes.
 * Search queries match against the local cache for instant results.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { unifiedApi } from '@/lib/api-client'
import { agentHostService } from '@/services/agentHostService'
import { atomicRulesService } from '@/services/atomic-rules.service'
import { API_CONFIG } from '@/lib/api-config'
import { queryPolicies } from '@/shared/api/query-policies'
import { useAuth } from '@/shared/auth'

// --- Types ---

export type EntityType = 'mission' | 'rule' | 'agent' | 'host' | 'user' | 'resource' | 'label' | 'page'

export interface IndexedEntity {
  id: string
  name: string
  type: EntityType
  status?: string
  route: string // admin-ui route to navigate to
  metadata?: Record<string, unknown>
}

/**
 * User behavioral signals for personalized ranking.
 * Fetched from governance-service audit trail.
 */
export interface UserSignals {
  /** Recently viewed entities (most recent first) */
  recentEntities: Array<{ id: string; lastSeen: string }>
  /** Entity interaction frequency (entity_id -> 0.0-1.0 normalized score) */
  entityInteractions: Record<string, number>
  /** Type affinity scores (type -> 0.0-1.0 normalized preference) */
  typeAffinity: Record<string, number>
}

interface EntityCatalog {
  entities: IndexedEntity[]
  signals: UserSignals | null
  fetchedAt: number
}

// --- Static Page Routes ---

/**
 * Static page routes for navigational search.
 * These are always available, even if API calls fail.
 */
const PAGE_ROUTES: IndexedEntity[] = [
  { id: 'page-missions', name: 'Missions', type: 'page', route: '/missions' },
  { id: 'page-mission-designer', name: 'Mission Designer', type: 'page', route: '/missions/designer' },
  { id: 'page-agents', name: 'Agents', type: 'page', route: '/agents' },
  { id: 'page-governance', name: 'Policy Center', type: 'page', route: '/governance' },
  { id: 'page-governance-designer', name: 'Governance Designer', type: 'page', route: '/governance/designer' },
  { id: 'page-trust', name: 'Trust Settings', type: 'page', route: '/governance/trust' },
  { id: 'page-resources', name: 'Resources', type: 'page', route: '/governance/resources' },
  { id: 'page-providers', name: 'Model Providers', type: 'page', route: '/governance/resources/providers' },
  { id: 'page-escalations', name: 'Escalations', type: 'page', route: '/governance/escalations' },
  { id: 'page-intelligence', name: 'Intelligence', type: 'page', route: '/intelligence' },
  { id: 'page-capsules', name: 'Data Capsules', type: 'page', route: '/intelligence/capsules' },
  { id: 'page-taxonomy', name: 'Taxonomy', type: 'page', route: '/intelligence/taxonomy' },
  { id: 'page-vocabulary', name: 'Vocabulary', type: 'page', route: '/intelligence/vocabulary' },
  { id: 'page-quality', name: 'Quality Dashboard', type: 'page', route: '/intelligence/quality' },
  { id: 'page-graph', name: 'Knowledge Graph Explorer', type: 'page', route: '/intelligence/graph' },
  { id: 'page-infrastructure', name: 'Infrastructure', type: 'page', route: '/infrastructure' },
  { id: 'page-hosts', name: 'Edge Hosts', type: 'page', route: '/infrastructure/hosts' },
  { id: 'page-clusters', name: 'Clusters', type: 'page', route: '/infrastructure/clusters' },
  { id: 'page-audit', name: 'Audit Log', type: 'page', route: '/audit' },
  { id: 'page-users', name: 'Users & Team', type: 'page', route: '/users' },
  { id: 'page-organization', name: 'Organization Settings', type: 'page', route: '/organization' },
  { id: 'page-settings', name: 'Settings', type: 'page', route: '/settings' },
  { id: 'page-profile', name: 'Profile', type: 'page', route: '/profile' },
  { id: 'page-billing', name: 'Billing', type: 'page', route: '/billing' },
  { id: 'page-analytics', name: 'Analytics Dashboard', type: 'page', route: '/analytics' },
]

// --- Query Keys ---

export const entityCatalogKeys = {
  all: ['entity-catalog'] as const,
  list: () => [...entityCatalogKeys.all, 'list'] as const,
}

// --- User Signals Fetcher ---

/**
 * Fetch user behavioral signals from governance-service.
 * Used for personalized entity ranking based on interaction history.
 *
 * @param userId - The current user's ID
 * @returns UserSignals or null if unavailable (graceful degradation)
 */
async function fetchUserSignals(userId: string): Promise<UserSignals | null> {
  try {
    const apiBase = API_CONFIG.services.unifiedGateway || API_CONFIG.baseURL
    const response = await fetch(
      `${apiBase}/v1/governance/audit/user-signals/${userId}?window=168h`,
      { credentials: 'include' }
    )
    if (!response.ok) return null

    const result = await response.json()

    // Handle both camelCase and snake_case responses (API may return either)
    const data = result.data ?? result
    return {
      recentEntities: data.recentEntities ?? data.recent_entities ?? [],
      entityInteractions: data.entityInteractions ?? data.entity_interactions ?? {},
      typeAffinity: data.typeAffinity ?? data.type_affinity ?? {},
    }
  } catch {
    // Graceful degradation: signals are optional for search functionality
    return null
  }
}

// --- Catalog Fetcher ---

/**
 * Fetch entities from multiple APIs in parallel.
 * Uses Promise.allSettled for graceful degradation if some APIs fail.
 *
 * @param userId - Optional user ID for fetching behavioral signals
 */
async function fetchEntityCatalog(userId?: string): Promise<EntityCatalog> {
  const [missions, agents, hosts, rules, signalsResult] = await Promise.allSettled([
    unifiedApi.getMissions(),
    unifiedApi.getAgents({ limit: 500 }),
    agentHostService.listHosts(),
    atomicRulesService.listRules(),
    userId ? fetchUserSignals(userId) : Promise.resolve(null),
  ])

  const entities: IndexedEntity[] = []

  // Missions
  if (missions.status === 'fulfilled') {
    const missionList = Array.isArray(missions.value) ? missions.value : []
    for (const m of missionList) {
      entities.push({
        id: m.id,
        name: m.name || m.id,
        type: 'mission',
        status: m.status || m.state,
        route: `/missions/${m.id}`,
      })
    }
  }

  // Agents
  if (agents.status === 'fulfilled') {
    const agentList = agents.value?.agents ?? []
    for (const a of agentList) {
      const agentId = a.id || a.did
      entities.push({
        id: agentId,
        name: a.displayName || a.name || agentId,
        type: 'agent',
        status: a.status || a.lifecycleState,
        route: `/agents/${agentId}`,
        metadata: { framework: a.framework, agentType: a.agentType },
      })
    }
  }

  // Hosts (Edge)
  if (hosts.status === 'fulfilled') {
    const hostList = hosts.value?.hosts ?? []
    for (const h of hostList) {
      entities.push({
        id: h.id,
        name: h.name || h.id,
        type: 'host',
        status: h.status,
        route: `/hosts/${h.id}`,
        metadata: { hostType: h.hostType },
      })
    }
  }

  // Rules
  if (rules.status === 'fulfilled') {
    const ruleList = rules.value?.rules ?? []
    for (const r of ruleList) {
      const ruleId = String(r.id)
      entities.push({
        id: ruleId,
        name: r.name || ruleId,
        type: 'rule',
        status: r.enabled ? 'enabled' : 'disabled',
        route: `/governance/rules/${ruleId}`,
        metadata: { action: r.action },
      })
    }
  }

  // Add static page routes (always available)
  entities.push(...PAGE_ROUTES)

  // Extract signals (graceful degradation if unavailable)
  const signals = signalsResult.status === 'fulfilled' ? signalsResult.value : null

  return {
    entities,
    signals,
    fetchedAt: Date.now(),
  }
}

// --- Hooks ---

/**
 * Hook to fetch and cache the entity catalog.
 *
 * The catalog is cached for 5 minutes and refreshed in the background.
 * Query is garbage collected after 30 minutes of inactivity.
 *
 * Includes user behavioral signals for personalized ranking when authenticated.
 */
export function useEntityCatalog() {
  const { user } = useAuth()
  const userId = user?.id

  return useQuery<EntityCatalog, Error>({
    queryKey: [...entityCatalogKeys.list(), userId ?? 'anonymous'],
    queryFn: () => fetchEntityCatalog(userId ?? undefined),
    ...queryPolicies.catalog,
  })
}

/**
 * Calculate behavioral boost for an entity based on user signals.
 *
 * Boost values are additive on top of text-matching scores (0-3 range).
 * Higher than server-side RRF values because client-side text scores are 0-3, not 0-1.
 *
 * @param entityId - The entity's unique identifier
 * @param entityType - The entity's type (mission, agent, etc.)
 * @param signals - User behavioral signals (null if unavailable)
 * @returns Additive boost value (0.0 to ~2.6 max)
 */
function calculateBehavioralBoost(
  entityId: string,
  entityType: EntityType,
  signals: UserSignals | null
): number {
  if (!signals) return 0

  let boost = 0

  // Recency: recently interacted entities get a time-decaying boost
  // Max 1.5, decays with half-life of 24 hours
  const recent = signals.recentEntities?.find((r) => r.id === entityId)
  if (recent) {
    const hoursAgo = (Date.now() - new Date(recent.lastSeen).getTime()) / 3600000
    boost += 1.5 * Math.exp((-hoursAgo * Math.LN2) / 24)
  }

  // Frequency: entities the user interacts with often
  // Max 0.8, directly proportional to interaction score
  const freq = signals.entityInteractions?.[entityId]
  if (freq) {
    boost += 0.8 * freq
  }

  // Type affinity: boost entity types the user prefers
  // Max 0.3, directly proportional to type affinity score
  const affinity = signals.typeAffinity?.[entityType]
  if (affinity) {
    boost += 0.3 * affinity
  }

  return boost
}

/**
 * Search the entity index with fuzzy matching and personalized ranking.
 *
 * Returns up to 10 results sorted by relevance score:
 * - Score 3: Exact prefix match on name
 * - Score 2: Name/type contains query word
 * - Score 1: Type prefix match
 * - Score 0.5: Fuzzy character match (all query chars appear in order)
 *
 * Behavioral boosts (when user signals available):
 * - Recency: +1.5 max for recently viewed (decays over 24h)
 * - Frequency: +0.8 max for frequently used entities
 * - Type affinity: +0.3 max for preferred entity types
 *
 * Multi-word queries: all words must match somewhere in name+type.
 * Word order doesn't matter ("log audit" finds "Audit Log").
 *
 * @param query - Search query (minimum 2 characters)
 * @returns Array of matching entities sorted by relevance
 */
export function useEntityIndex(query: string): IndexedEntity[] {
  const { data: catalog } = useEntityCatalog()

  const results = useMemo(() => {
    if (!catalog || !query || query.length < 2) {
      return []
    }

    const { signals } = catalog
    const lowerQuery = query.toLowerCase().trim()
    const queryWords = lowerQuery.split(/\s+/).filter((w) => w.length > 0)

    const scored = catalog.entities
      .map((entity) => {
        const name = entity.name.toLowerCase()
        const type = entity.type.toLowerCase()
        const combined = `${name} ${type}` // search across name + type

        let textScore: number | null = null

        // Single word: use original logic
        if (queryWords.length === 1) {
          const word = queryWords[0]!
          // Exact prefix match gets highest score
          if (name.startsWith(word)) {
            textScore = 3
          }
          // Name contains query
          else if (name.includes(word)) {
            textScore = 2
          }
          // Type matches
          else if (type.startsWith(word)) {
            textScore = 1
          } else {
            // Fuzzy: check if all query chars appear in order
            let queryIndex = 0
            for (const char of name) {
              if (char === word[queryIndex]) {
                queryIndex++
              }
              if (queryIndex === word.length) {
                textScore = 0.5
                break
              }
            }
          }
        } else {
          // Multi-word: all words must match somewhere in name+type
          let totalScore = 0
          let allMatch = true
          for (const word of queryWords) {
            if (name.startsWith(word)) {
              totalScore += 3
            } else if (combined.includes(word)) {
              totalScore += 2
            } else {
              // Fuzzy: check if word chars appear in order in name
              let qi = 0
              let found = false
              for (const ch of name) {
                if (ch === word[qi]) qi++
                if (qi === word.length) {
                  found = true
                  break
                }
              }
              if (found) {
                totalScore += 0.5
              } else {
                allMatch = false
                break
              }
            }
          }

          if (allMatch) {
            // Average score across words
            textScore = totalScore / queryWords.length
          }
        }

        // No text match = not a result
        if (textScore === null) return null

        // Add behavioral boost (additive, same as server-side)
        const behavioralBoost = calculateBehavioralBoost(entity.id, entity.type, signals)
        const finalScore = textScore + behavioralBoost

        return { entity, score: finalScore }
      })
      .filter((r): r is { entity: IndexedEntity; score: number } => r !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map((r) => r.entity)

    return scored
  }, [catalog, query])

  return results
}
